import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createSelectors } from '@trg/utils';
import {
  MOST_RECENT_SEARCH_KEYWORDS,
  MOST_RECENT_BROWSE_PRODUCTS,
} from '../../constants';

interface SearchState {
  keyword: string;
  isSearchOpen: boolean;
  recentSearch: string[];
  recentBrowse: string[];
}

interface SearchActions {
  setKeyword: (keyword: string) => void;
  setIsSearchOpen: (open: boolean) => void;
  addRecentSearch: (keyword: string) => void;
  clearRecentSearch: () => void;
  addRecentBrowse: (sku: string) => void;
  clearRecentBrowse: () => void;
}

type SearchStore = SearchState & SearchActions;

const useSearchStoreBase = create<SearchStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        keyword: '',
        isSearchOpen: false,
        recentSearch: [],
        recentBrowse: [],
        setKeyword(keyword) {
          set({ keyword });
        },
        setIsSearchOpen(open) {
          set({ isSearchOpen: open });
        },
        addRecentSearch(keyword) {
          set(({ recentSearch }) => {
            if (!keyword) return;
            const index = recentSearch.indexOf(keyword);
            index !== -1 && recentSearch.splice(index, 1);
            recentSearch.unshift(keyword);
            if (recentSearch.length > MOST_RECENT_SEARCH_KEYWORDS) {
              recentSearch.pop();
            }
          });
        },
        clearRecentSearch() {
          set({ recentSearch: [] });
        },
        addRecentBrowse(sku) {
          set(({ recentBrowse }) => {
            if (!sku) return;
            const index = recentBrowse.indexOf(sku);
            index !== -1 && recentBrowse.splice(index, 1);
            recentBrowse.unshift(sku);
            if (recentBrowse.length > MOST_RECENT_BROWSE_PRODUCTS) {
              recentBrowse.pop();
            }
          });
        },
        clearRecentBrowse() {
          set({ recentBrowse: [] });
        },
      })),
      {
        name: '__tlw_search',
        partialize: ({ recentSearch, recentBrowse }) => ({
          recentSearch,
          recentBrowse,
        }),
      }
    ),
    {
      name: 'search',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

export const useSearchStore = createSelectors(useSearchStoreBase);
